import {
  Avatar,
  Button,
  Card,
  Divider,
  Dropdown,
  Flex,
  Form,
  Input,
  InputNumber,
  MenuProps,
  Tag,
  Typography
} from 'antd';
import { CustomBreadcrumb } from 'components/CustomBreadcrumb';
import {
  DownloadOutlined,
  EllipsisOutlined,
  HistoryOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { EditLayout } from 'components/layout/layout/EditLayout';
import { FormItemCol } from 'components/professor_area/FormItemCol';
import { HttpError, useBack, useOne } from '@refinedev/core';
import { getInitialsName } from 'utils/get-initials-name';
import { handleWizardError } from 'utils/error-handler';
import { useCallback, useMemo } from 'react';
import { useForm } from '@refinedev/antd';
import { useParams } from 'react-router-dom';
import { useProfessorAreaAssignmentsStore } from '../assignments.store';
import type {
  IProfessorAreaEvaluationAttemptCorrection,
  IProfessorAreaEvaluationAttemptShow
} from 'interfaces/professor_area/evaluations';
import type { IUser } from 'interfaces/users';

const itemsDropdown: MenuProps['items'] = [
  {
    key: 'info',
    icon: <InfoCircleOutlined />,
    label: 'Infomações da atividade'
  },
  {
    key: 'history',
    icon: <HistoryOutlined />,
    label: 'Visualizar histórico'
  }
];

export const ProfessorAreaAssignmentAttemptEdit = () => {
  const params = useParams();
  const classroomId = params.classroomId;
  const evaluationId = params.evaluationId;
  const id = params.id;

  const back = useBack();

  const selectedEvaluation = useProfessorAreaAssignmentsStore(
    s => s.selectedEvaluation
  );

  const { formProps, queryResult } = useForm<
    IProfessorAreaEvaluationAttemptShow,
    HttpError,
    IProfessorAreaEvaluationAttemptCorrection
  >({
    resource: 'professor_area/classroom_assignments/attempts',
    meta: { classroomId, evaluationId },
    action: 'edit',
    id,
    // Explicit so a failed save always reports itself (the submit path relies
    // on this instead of catching the mutation locally).
    errorNotification: () => ({
      type: 'error',
      message: 'Não foi possível salvar a correção. Tente novamente!',
      description: 'Algo deu errado!'
    })
  });

  const { data: studentDetails } = useOne<IUser>({
    resource: `users`,
    id: queryResult?.data?.data?.user_id || ''
  });

  const studentName = useMemo(() => {
    if (studentDetails) {
      return studentDetails.data.name;
    }

    return '';
  }, [studentDetails]);

  const { isLoading, isFetched, data } = queryResult!;
  const record = useMemo(() => data?.data, [data]);

  // Use `!= null` so a legitimate grade of 0 counts as corrected (not `!!score`,
  // which treats 0 as ungraded and re-opens the form).
  const isEdit = useMemo(() => record?.score != null, [record]);

  const menu = {
    items: itemsDropdown,
    onClick: () => null
  };

  const onSubmit = useCallback(async () => {
    const form = formProps.form;

    if (!form) {
      return;
    }

    try {
      // validateFields already enforces that score (including 0) and feedback
      // are present; the old `score && feedback` guard silently dropped a grade of 0.
      await form.validateFields();
    } catch (error: unknown) {
      // Same treatment as the other professor-area forms: scroll to the
      // offending field instead of a generic toast that doesn't say what's wrong.
      handleWizardError(form, error);
      return;
    }

    // Submission errors surface through the useForm errorNotification.
    form.submit();
  }, [formProps.form]);

  const onDownloadStudentAnswer = useCallback((url?: string) => {
    if (!url) {
      return;
    }

    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  return (
    <EditLayout
      isFetched={isFetched}
      isLoading={isLoading}
      record={record}
      breadcrumb={
        <CustomBreadcrumb
          label="Área do professor"
          backButton
          items={[
            {
              label: 'Turmas',
              path: '/professor_area/classrooms'
            },
            {
              label: 'Visualizar turma',
              path: `/professor_area/classrooms/show/${classroomId}?tab=assignments`
            },
            {
              label: 'Atividades'
            },
            {
              label: 'Atividade do aluno',
              path: `/professor_area/classrooms/${classroomId}/assignments/edit/${evaluationId}?tab=attempts`
            },
            {
              label: isEdit ? 'Visualizar correção' : 'Correção'
            }
          ]}
        />
      }
      footerButtons={saveButtonProps => (
        <Flex gap={8}>
          <Button onClick={() => back()} disabled={isLoading}>
            Voltar
          </Button>
          {!isEdit && (
            <Button
              type="primary"
              {...saveButtonProps}
              onClick={onSubmit}
              disabled={isLoading}
            >
              Salvar correção
            </Button>
          )}
        </Flex>
      )}
    >
      <Card>
        <Flex align="center" justify="space-between">
          <Flex vertical gap={8} flex={1}>
            <Flex align="center" gap={8}>
              <Avatar className="bg-primary-500/10 text-primary-500">
                {getInitialsName(studentName)}
              </Avatar>
              <Typography.Text className="text-gray-400">
                {selectedEvaluation?.user?.name}
              </Typography.Text>
            </Flex>

            <Typography.Title level={3} className="!mb-0">
              {selectedEvaluation?.evaluation.name}
            </Typography.Title>

            <Flex>
              <Tag>Atividade</Tag>
              <Tag>{selectedEvaluation?.classroom.code}</Tag>
              <Tag>{selectedEvaluation?.classroom.discipline}</Tag>
            </Flex>
          </Flex>

          <Dropdown menu={menu}>
            <Button icon={<EllipsisOutlined />} />
          </Dropdown>
        </Flex>
      </Card>

      <Card>
        <Form {...formProps} className="max-w-3xl">
          <Typography.Text
            className="block font-semibold mb-6"
            type="secondary"
          >
            Respostas
          </Typography.Text>

          {record?.criteria_files?.map((item, index) => {
            const criteriaItems = item?.items ?? [];
            const hasDocuments = criteriaItems.length > 0;

            return (
              <FormItemCol
                key={item.id}
                label={`Critério ${index + 1}`}
                name="answer"
                itemsCenter={false}
              >
                <Flex vertical gap={8} className="flex-start">
                  <Input value={item.name} readOnly disabled />
                  {hasDocuments ? (
                    criteriaItems.map((studentAnswer, answerIndex) => (
                      <Button
                        key={
                          studentAnswer?.id ??
                          `${item.id}-answer-${answerIndex + 1}`
                        }
                        icon={<DownloadOutlined />}
                        className="self-start"
                        disabled={!studentAnswer?.url}
                        onClick={() =>
                          onDownloadStudentAnswer(studentAnswer?.url)
                        }
                      >
                        {studentAnswer?.name || 'Resposta do aluno'}
                      </Button>
                    ))
                  ) : (
                    <Button
                      icon={<DownloadOutlined />}
                      className="self-start"
                      disabled
                    >
                      Resposta do aluno
                    </Button>
                  )}
                </Flex>
              </FormItemCol>
            );
          })}

          <Divider />

          <FormItemCol
            label="Nota (0-100)"
            name="score"
            itemsCenter
            rules={[
              { required: true, message: 'Campo obrigatório' },
              {
                type: 'number',
                min: 0,
                max: 100,
                message: 'A nota deve estar entre 0 e 100'
              }
            ]}
          >
            <InputNumber
              className="max-w-[80px]"
              size="large"
              min={0}
              max={100}
              disabled={isEdit}
            />
          </FormItemCol>

          <FormItemCol
            label="Comentário"
            name="feedback"
            itemsCenter={false}
            rules={[{ required: true, message: 'Campo obrigatório' }]}
          >
            <Input.TextArea rows={4} size="large" disabled={isEdit} />
          </FormItemCol>
        </Form>
      </Card>
    </EditLayout>
  );
};
